<template>
  <v-row no-gutters>
    <v-col>
      <v-card-text class="pl-0 pt-0 pb-0 ds-margin-bottom-stack--xs ds-font-size--md ds-font-weight--semibold">
        {{ $t('components.PublishSwitch.label.title.published') }}
      </v-card-text>
    </v-col>

    <v-col
      v-if="hasUnsharableCard"
      cols="12"
      class="ds-margin-bottom-stack--sm ds-margin-top-stack--nano"
    >
      <v-card-text class="pl-0 pt-0 pb-0 publish-container__description">
        {{ $t('components.PublishSwitch.label.title.description.dashboardHidden') }} <a
          :href="centralLink"
          target="_blank"
          class="publish-container__link"
        >{{ $t('components.PublishSwitch.label.title.description.dashboardHiddenCentralLink') }}</a>
        {{ $t('components.PublishSwitch.label.title.description.dashboardHiddenAux') }} <a
          :href="supportLink"
          target="_blank"
          class="publish-container__link"
        >{{ $t('components.PublishSwitch.label.title.description.dashboardHiddenPolicyLink') }}</a>
      </v-card-text>
    </v-col>

    <v-col cols="12">
      <span
        v-tooltip.top-end="publishSwitchTooltip"
        class="switch-container"
      >
        <DsSwitch
          v-model="isPublished"
          :label="$t('components.PublishSwitch.label.switch')"
        />
      </span>
    </v-col>

    <v-col
      v-if="hasUnsharableCard"
      cols="12"
      class="ds-margin-bottom-stack--sm ds-margin-top-stack--nano"
    >
      <v-card-text class="pl-0 py-0 ds-margin-bottom-stack--nano ds-font-size--md ds-font-weight--semibold">
        {{ $t('components.PublishSwitch.label.title.hiddenComponents') }}
      </v-card-text>

      <v-row>
        <v-col
          class="pl-3 pt-2"
        >
          <v-simple-table
            fixed-header
            class="publish-container__table"
          >
            <template v-slot:default>
              <thead>
                <tr>
                  <th class="text-left ds-font-size--xs ds-font-weight--semibold">
                    {{ $t('components.PublishSwitch.label.panel.components') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(component, index) in showFilteredComponents()"
                  :key="index"
                >
                  <td class="ds-font-size--sm">
                    {{ component.props.title }}
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <div
            v-show="hasPagination"
            class="publish-container__pagination text-center ds-margin-top-stack--xs"
          >
            <div class="text-right ds-font-size--sm">
              {{ $t('components.PublishSwitch.label.pagination', {
                page: page,
                size: paginationSize,
                count: countUnsharableComponents
              }) }}
            </div>
            <v-pagination
              v-model="page"
              circle
              total-visible="5"
              :length="paginationSize"
            />
          </div>
        </v-col>
        <v-spacer />
      </v-row>

      <DsButton
        class="mt-5"
        @click="openRemoveCardModal"
      >
        {{ $t('components.PublishSwitch.label.button.remove') }}
      </DsButton>
    </v-col>

    <v-col
      v-if="publisher.isPublished"
      cols="12"
    >
      <v-row>
        <v-col
          cols="6"
          class="text-field-url"
        >
          <v-text-field
            :disabled="true"
            :value="dashboardUrl"
            :label="$t('components.PublishSwitch.label.url')"
          />
        </v-col>
        <v-row
          no-gutters
          @click="trackCopyUrl"
        >
          <CopyToClipboard
            :content="dashboardUrl"
          />
        </v-row>
      </v-row>

      <div>
        <b>{{ $t('components.PublishSwitch.label.title.publishedBy') }}</b>
        <br><br>
        {{ publisher.publishedBy }}

        <br><br><br>

        <b>{{ $t('components.PublishSwitch.label.title.publishedAt') }}</b>
        <br><br>
        {{ fullDate(publisher.publishedAt) }}

        <br><br><br>

        <b>{{ $t('components.PublishSwitch.label.title.token') }}</b>
      </div>
      <v-row>
        <v-col
          cols="6"
          class="text-field-url"
        >
          <v-text-field
            :disabled="true"
            :value="publisher.publishedToken"
            :label="$t('components.PublishSwitch.label.token')"
          />
        </v-col>
        <CopyToClipboard
          :content="publisher.publishedToken"
        />
      </v-row>

      <v-row>
        <DsButton
          class="ml-3"
          @click="republish"
        >
          {{ $t('components.PublishSwitch.label.button.regenerateToken') }}
        </DsButton>
      </v-row>
    </v-col>

    <RemoveCardModal
      v-if="showRemoveCardModal"
      v-model="showRemoveCardModal"
      :count="countUnsharableComponents"
      @enableSharing="enableSharing"
    />
  </v-row>
</template>

<script>
import cardsModel from '@/mixins/cards-model';
import RemoveCardModal from '@/components/RemoveCardModal';
import CopyToClipboard from '@/components/CopyToClipboard';
import { MUTATE } from '@/store/modules/mutation_types';
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex';
import { humanizeDate, fullDate } from '@/utils/date';
import { DsButton, DsSwitch } from '@ds/components';
import { StFeatureFlagFlags } from '@/plugins/rollout';
import { trackEventWithUserProperties } from '@/utils/mixpanel';

export default {
  name: 'PublishSwitch',
  components: {
    RemoveCardModal,
    CopyToClipboard,
    DsButton,
    DsSwitch,
  },
  mixins: [
    cardsModel,
  ],
  data() {
    return {
      showRemoveCardModal: false,
      page: 1,
      componentsPerPage: 5,
      centralLink: 'https://suporte.stilingue.com.br/hc/pt-br/sections/4446049457948',
      supportLink: 'https://suporte.stilingue.com.br/hc/pt-br/articles/4406728962450',
    };
  },
  computed: {
    ...mapGetters('dashboard', [
      'activeDashboard',
      'unsharableCards',
    ]),
    ...mapState('user', [
      'user',
    ]),
    ...mapGetters('filter', [
      'formattedDateRange',
    ]),
    ...mapGetters('featureFlag', [
      'isEnabled',
    ]),
    publishedTooltip() {
      return `${this.$t('components.PublishSwitch.label.tooltip.publishedBy')} ${this.publisher.publishedBy} ${humanizeDate(this.publisher.publishedAt)}`;
    },
    canChangePublished() {
      return this.user && this.activeDashboard ? this.publisher.users.includes(this.user.email) : false;
    },
    publisher() {
      return {
        isPublished: this.activeDashboard.data.isPublished,
        publishedBy: this.activeDashboard.data.publishedBy,
        publishedAt: this.activeDashboard.data.publishedAt,
        publishedToken: this.activeDashboard.data.publishedToken,
        users: this.activeDashboard.data.users || this.activeDashboard.data.firstAccessUser,
      };
    },
    isPublished: {
      get() {
        return this.publisher.isPublished;
      },
      set(newValue) {
        newValue ? this.turnOnPublish() : this.turnOffPublish();

        trackEventWithUserProperties('ST Settings ShareToggle On/Off', { On: newValue });
      },
    },
    publishSwitchTooltip() {
      if (!this.canChangePublished) return this.$t('components.PublishSwitch.label.tooltip.notPermitted');
      return this.publisher.isPublished
        ? this.publishedTooltip
        : this.$t('components.PublishSwitch.label.tooltip.notPublished');
    },
    dashboardUrl() {
      return `${window.location.origin}${window.location.pathname}?public=1&date_range=${this.formattedDateRange}&dashboard=${this.activeDashboard.id}`;
    },
    paginationSize() {
      return Math.ceil(this.countUnsharableComponents / this.componentsPerPage);
    },
    hasPagination() {
      return this.paginationSize > 1;
    },
    showSharingFeature() {
      return !this.publisher.isPublished && this.hasUnsharableCard;
    },
    countUnsharableComponents() {
      return this.unsharableCards.length;
    },
    hasUnsharableCard() {
      return this.countUnsharableComponents !== 0 && StFeatureFlagFlags.shareManagement.isEnabled();
    },
  },
  methods: {
    fullDate,
    ...mapActions('dashboard', [
      'turnOnPublish',
      'turnOffPublish',
    ]),
    ...mapMutations('misc', {
      mutateMisc: MUTATE,
    }),
    republish() {
      this.turnOnPublish();
      this.$noty.success(this.$t('components.PublishSwitch.notification.success.token'));
    },
    openRemoveCardModal() {
      this.showRemoveCardModal = true;
      this.mutateMisc({
        property: 'showConfig',
        with: false,
      });
    },
    enableSharing() {
      this.deleteCard();
    },
    deleteCard() {
      this.activeDashboard.data.cards = this.activeDashboard.data.cards.filter(card => card.shareable !== false);

      this.saveCards();
    },
    showFilteredComponents() {
      const startOffset = (this.page - 1) * this.componentsPerPage;
      const endOffset = this.page * this.componentsPerPage;

      return this.unsharableCards.slice(startOffset, endOffset);
    },
    trackCopyUrl() {
      trackEventWithUserProperties('ST Settings ShareLink Copy');
    },
  },
};
</script>

<style lang="scss">
  .publish-container {
    display: grid;
    grid-template-columns: 500px 30px;

    &__description {
      color: var(--ds-color-text-2);
      margin-bottom: var(--ds-spacing-stack-xs);
    }

    &__table {
      table {
        border: 1px solid var(--ds-color-silver);
        border-radius: 4px;
        overflow: hidden;
      }

      th,
      td {
        height: 56px !important;
      }

      .theme--light & th {
        color: var(--ds-color-text-2) !important;
        background-color: var(--ds-color-background) !important;
      }
    }

    &__pagination {
      display: flex;
      justify-content: right;
      align-items: center;
    }

    &__link {
      font-weight: var(--ds-font-weight-semibold);
      text-decoration: underline;
    }

    .text-field-url {
      width: 50%;
      max-width: calc(100% - 50px);
    }
  }

  .switch-container {
    display: inline-block;
    margin-bottom: var(--ds-spacing-stack-lg);
  }
</style>
